// src/admin/sections/CravingsSection.jsx
//
// Ticket 37. Admin: Cravings, the "All cravings" view. A log to read, not a queue
// to clear: what readers typed into the craving box, in their own words. It is
// where good examples get picked for Overheard, and where you see what the
// catalogue is missing (search "dragons" or "grief" and read how readers phrase
// it; sort by Worst matched first and read what it served worst).
//
// THE TEXT IS NOT READABLE FROM THE BROWSER, BY DESIGN. Since ticket 27
// (20260930000000_ticket27_honest_empty_state.sql) the client can read back `id`
// and nothing else from `cravings`. So nothing here queries that table. The list
// and the stat cards come from two admin-only database functions
// (20261019100000_ticket37_admin_cravings.sql): admin_list_cravings and
// admin_craving_stats. Search, the filters and the sort all run inside them.
//
// THE PRIVACY RULE LIVES IN THOSE FUNCTIONS. A signed-in craving is stored with
// the reader's account id (CravingBox.jsx:209-217). Neither function selects,
// returns or joins it, so this page is never handed it and cannot show or look it
// up. This page must never say "no reader attached": it is not true for a
// signed-in craving, and the database does not tell us either way. The one line
// above the list says what is true. (scripts/check-cravings-admin.mjs fails the
// build if either rule is undone.)
//
// EVERY NUMBER IS A SERVER COUNT. A count that fails shows "—", never 0.
//
// "NEEDS A LOOK" is the database's pattern match for an email address, phone
// number, link or @handle (craving_needs_look). Those can't go on Overheard, and
// the disabled button says why in words on the row, not only in a tooltip.
//
// ONE ACTION PER ROW, written straight to overheard_items under the admin policy
// that table already has (20261009120000_overheard_items.sql):
//   Add to Overheard      inserts the craving's words (source 'reader_words',
//                         craving_id set), switched on.
//   Remove from Overheard deletes that craving's item.
// "On Overheard" means an item exists for the craving, switched on or off
// (ticket 38 can switch one off). Both writes look at the database's answer: a
// refused or empty write is reported, never shown as done.

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { useTabParam } from '../../hooks/useTabParam';
import { MIN_CRAVING_WORDS, wordsOf } from '../../lib/cravingWords';
import { clearsThreshold } from '../../lib/matchThreshold';
import { logAudit } from '../lib/auditLog';
import InfoTip from '../components/InfoTip';

const PAGE = 25;

const FILTERS = [
  { id: 'all',        label: 'All' },
  { id: 'overheard',  label: 'On Overheard' },
  { id: 'needs_look', label: 'Needs a look' },
];
const SORTS = [
  { id: 'newest', label: 'Newest first' },
  { id: 'worst',  label: 'Worst matched first' },
];

const fmt = n => (n === null || n === undefined ? '—' : n.toLocaleString());

const TONE = {
  ok:   { bg: 'var(--feedback-success-bg)', color: 'var(--feedback-success)', border: 'var(--feedback-success)' },
  warn: { bg: 'var(--feedback-warning-bg)', color: 'var(--feedback-warning-ink)', border: 'var(--feedback-warning-border)' },
  bad:  { bg: 'var(--feedback-error-bg)', color: 'var(--feedback-error)', border: 'var(--feedback-error)' },
};

// Why a row's Add button is off, in words. null = it can be added.
export function cannotAddReason(row) {
  if (row.needs_look) {
    return "Can't go on Overheard: it looks like it contains an email address, phone number, link or @handle, and Overheard is shown to every reader.";
  }
  const n = wordsOf(row.craving_text);
  if (n < MIN_CRAVING_WORDS) {
    return `Can't go on Overheard: it is ${n} word${n === 1 ? '' : 's'}, and an Overheard example needs at least ${MIN_CRAVING_WORDS}.`;
  }
  return null;
}

// What the database said when an Add was refused, in words.
export function describeAddError(error) {
  if (error?.code === '23505') {
    return 'Not added: Overheard already has an item with exactly these words, from another craving.';
  }
  if (error?.code === '23514') {
    return `Not added: the database refused it (an Overheard item needs at least ${MIN_CRAVING_WORDS} words).`;
  }
  return `Not added: ${error?.message || 'the database refused it'}.`;
}

function Pill({ children, tone = 'neutral' }) {
  const t = TONE[tone] || { bg: '#f5f0e8', color: 'var(--neutral-600)', border: 'var(--brand-sand)' };
  return (
    <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, padding: '2px 8px', borderRadius: 'var(--radius-md)', background: t.bg, color: t.color, border: `1px solid ${t.border}`, whiteSpace: 'nowrap' }}>
      {children}
    </span>
  );
}

function Btn({ children, onClick, disabled, primary, isMobile, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        padding: isMobile ? '0 16px' : '7px 14px', minHeight: isMobile ? 44 : undefined,
        background: primary ? 'var(--brand-forest)' : 'var(--brand-white)',
        color: primary ? 'var(--brand-white)' : 'var(--neutral-600)',
        border: primary ? 'none' : '1px solid var(--brand-sand)',
        borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-xs)', fontWeight: 700,
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1, fontFamily: 'var(--font-body)',
      }}
    >
      {children}
    </button>
  );
}

function Stat({ label, tip, value, sub }) {
  return (
    <div style={{ background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-lg)', padding: '14px 16px', minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--neutral-500)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}<InfoTip text={tip} />
      </div>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, color: 'var(--neutral-800)', lineHeight: 1.1, marginTop: 4 }}>{fmt(value)}</div>
      {sub && <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function matchPill(sim) {
  if (sim === null || sim === undefined) return <Pill>not scored</Pill>;
  const pct = Math.round(sim * 100);
  return clearsThreshold(sim)
    ? <Pill tone="ok">best match {pct}%</Pill>
    : <Pill tone="warn">best match {pct}% · below the bar</Pill>;
}

function CravingRow({ row, isMobile, busy, result, onAdd, onRemove, onSeeOverheard }) {
  const reason = row.on_overheard ? null : cannotAddReason(row);
  const words = wordsOf(row.craving_text);
  return (
    <div style={{ background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-lg)', padding: isMobile ? 12 : 14, marginBottom: 10 }}>
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 12, justifyContent: 'space-between' }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <p style={{ margin: 0, fontSize: 'var(--font-size-md)', color: 'var(--neutral-800)', lineHeight: 1.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {row.craving_text}
          </p>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 10 }}>
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)' }}>
              {new Date(row.created_at).toLocaleString()} · #{row.id} · {words} words
            </span>
            {row.on_overheard && <Pill tone="ok">on Overheard</Pill>}
            {row.needs_look && <Pill tone="warn">needs a look</Pill>}
            {matchPill(row.best_match_similarity)}
          </div>
        </div>
        <div style={{ flexShrink: 0 }}>
          {row.on_overheard ? (
            <Btn isMobile={isMobile} disabled={busy} onClick={() => onRemove(row)}>{busy ? 'Removing…' : 'Remove from Overheard'}</Btn>
          ) : (
            <Btn primary isMobile={isMobile} disabled={busy || !!reason} title={reason || undefined} onClick={() => onAdd(row)}>{busy ? 'Adding…' : 'Add to Overheard'}</Btn>
          )}
        </div>
      </div>
      {reason && (
        <div style={{ marginTop: 10, fontSize: 'var(--font-size-sm)', color: TONE.warn.color }}>{reason}</div>
      )}
      {result && (
        <div role="status" style={{ marginTop: 10, padding: '8px 12px', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', fontWeight: 600, background: TONE[result.tone].bg, color: TONE[result.tone].color, border: `1px solid ${TONE[result.tone].border}` }}>
          {result.text}
          {result.seeIt && (
            <button type="button" onClick={onSeeOverheard} style={{ marginLeft: 10, background: 'none', border: 'none', padding: 0, color: 'inherit', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: 'inherit' }}>
              See it in Overheard
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function CravingsSection({ isMobile, onSeeOverheard }) {
  const [filterParam, setFilter] = useTabParam('filter', 'all');
  const [sortParam, setSort] = useTabParam('sort', 'newest');
  const filter = FILTERS.some(f => f.id === filterParam) ? filterParam : 'all';
  const sort = SORTS.some(s => s.id === sortParam) ? sortParam : 'newest';

  // What the admin is typing, and what the list is actually asking for. The list
  // asks 300ms after typing stops, so each keystroke is not a database call.
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const [stats, setStats] = useState(null);
  const [statsFailed, setStatsFailed] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [more, setMore] = useState(false);
  const [results, setResults] = useState({});
  const [busyIds, setBusyIds] = useState(() => new Set());

  // A newer load supersedes an older one: typing or switching a filter while a
  // list is loading must not paint the old answer under the new question.
  const loadId = useRef(0);

  const loadStats = useCallback(async () => {
    const { data, error } = await supabase.rpc('admin_craving_stats');
    const s = Array.isArray(data) ? data[0] : data;
    if (error || !s) {
      if (error) console.error('[Cravings] stats failed:', error.message);
      setStats(null);
      setStatsFailed(true);
      return;
    }
    // bigint arrives as a number or a string depending on the client; a missing
    // value stays unknown rather than becoming 0.
    const num = v => (v === null || v === undefined || Number.isNaN(Number(v)) ? null : Number(v));
    setStats({
      recorded: num(s.recorded), thisWeek: num(s.this_week), onOverheard: num(s.on_overheard), needsLook: num(s.needs_look),
      failedThisWeek: num(s.failed_this_week), failedToday: num(s.failed_today),
    });
    setStatsFailed(false);
  }, []);

  const loadRows = useCallback(async (from = 0) => {
    const id = ++loadId.current;
    if (from === 0) { setLoading(true); setListError(null); }
    // One more than a page, so "Show more" appears only when there is more.
    const { data, error } = await supabase.rpc('admin_list_cravings', {
      p_search: search || null,
      p_filter: filter,
      p_sort: sort,
      p_limit: PAGE + 1,
      p_offset: from,
    });
    if (id !== loadId.current) return;
    if (error) {
      console.error('[Cravings] list failed:', error.message);
      setListError(error.message);
      if (from === 0) setRows([]);
      setMore(false);
      setLoading(false);
      return;
    }
    const got = data || [];
    setRows(prev => (from === 0 ? got.slice(0, PAGE) : [...prev, ...got.slice(0, PAGE)]));
    setMore(got.length > PAGE);
    setLoading(false);
  }, [search, filter, sort]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadRows(0); }, [loadRows]);

  const setBusy = (id, on) => setBusyIds(prev => {
    const next = new Set(prev);
    if (on) next.add(id); else next.delete(id);
    return next;
  });
  const setResult = (id, r) => setResults(prev => ({ ...prev, [id]: r }));
  const patchRow = (id, patch) => setRows(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)));

  async function addToOverheard(row) {
    // The same rules the buttons show; checked again here so a stale screen can't
    // put contact details in front of every reader.
    const reason = cannotAddReason(row);
    if (reason) { setResult(row.id, { tone: 'warn', text: reason }); return; }
    setBusy(row.id, true);
    setResult(row.id, null);
    const { data: saved, error } = await supabase
      .from('overheard_items')
      .insert({ text: row.craving_text, source: 'reader_words', craving_id: row.id, is_active: true })
      .select('id')
      .single();
    if (error || !saved) {
      setResult(row.id, { tone: 'bad', text: describeAddError(error) });
      setBusy(row.id, false);
      return;
    }
    await logAudit('create', 'overheard_item', `Added reader craving #${row.id} to Overheard`, saved.id);
    patchRow(row.id, { on_overheard: true });
    setResult(row.id, { tone: 'ok', text: 'Added. It is showing on Overheard now.', seeIt: !!onSeeOverheard });
    setBusy(row.id, false);
    loadStats();
  }

  async function removeFromOverheard(row) {
    setBusy(row.id, true);
    setResult(row.id, null);
    const { data: gone, error } = await supabase
      .from('overheard_items')
      .delete()
      .eq('craving_id', row.id)
      .select('id');
    if (error) {
      setResult(row.id, { tone: 'bad', text: `Not removed: ${error.message}` });
      setBusy(row.id, false);
      return;
    }
    if (!gone?.length) {
      // Nothing was deleted: it was already gone (another admin, or ticket 38's
      // Delete). Say so and show the true state rather than "removed".
      patchRow(row.id, { on_overheard: false });
      setResult(row.id, { tone: 'warn', text: 'Nothing to remove: it was no longer on Overheard. The screen now shows that.' });
      setBusy(row.id, false);
      loadStats();
      return;
    }
    await logAudit('delete', 'overheard_item', `Removed reader craving #${row.id} from Overheard`, gone[0].id);
    patchRow(row.id, { on_overheard: false });
    setResult(row.id, { tone: 'ok', text: 'Removed. It is no longer on Overheard.' });
    setBusy(row.id, false);
    loadStats();
  }

  const s = stats || {};
  const chipCount = { all: s.recorded, overheard: s.onOverheard, needs_look: s.needsLook };
  const searching = search !== '';

  return (
    <div style={{ fontFamily: 'var(--font-body)' }}>
      {statsFailed && (
        <div role="alert" style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 'var(--radius-md)', background: TONE.warn.bg, color: TONE.warn.color, border: `1px solid ${TONE.warn.border}`, fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>
          Couldn&apos;t load the counts. A dash means unknown, not zero.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)', gap: 12, marginBottom: 18 }}>
        <Stat label="Recorded" value={s.recorded} tip="Every craving readers have typed into the craving box and run, signed in or signed out, since recording began. A craving is recorded when it is run; one that was never submitted is not here." />
        <Stat label="This week" value={s.thisWeek} tip="Cravings recorded in the last 7 days. A rolling 7 days, not Monday to Sunday." />
        <Stat label="On Overheard" value={s.onOverheard} tip="Cravings that have an item on Overheard, switched on or off. Examples we wrote ourselves are not cravings and are not counted here." />
        <Stat
          label="Searches that failed"
          value={s.failedThisWeek}
          sub={s.failedToday === null || s.failedToday === undefined ? undefined : `${fmt(s.failedToday)} in the last 24 hours`}
          tip="Cravings from the last 7 days that were saved with no matching data, because the step that turns the words into numbers failed. The reader got no matches. This failure is not reported anywhere else, so a number that climbs here means matching is down. The second line is the last 24 hours."
        />
      </div>

      <div style={{ background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-lg)', padding: isMobile ? '14px 12px' : '20px 22px' }}>
        <p style={{ margin: '0 0 14px', fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', lineHeight: 1.5 }}>
          These are the readers&apos; words as typed. This screen never shows who wrote them and never looks it up: a signed-in reader&apos;s craving is saved with their account, and that stays out of sight here.
        </p>

        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 10, marginBottom: 12 }}>
          <input
            type="search"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder="Search the words, e.g. dragons, grief"
            aria-label="Search cravings"
            style={{ flex: 1, minWidth: 0, padding: isMobile ? '0 12px' : '8px 12px', minHeight: isMobile ? 44 : undefined, border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', fontFamily: 'var(--font-body)', outline: 'none' }}
          />
          <select
            value={sort}
            onChange={e => setSort(e.target.value)}
            aria-label="Sort"
            style={{ padding: isMobile ? '0 12px' : '8px 12px', minHeight: isMobile ? 44 : undefined, border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', fontFamily: 'var(--font-body)', background: 'var(--brand-white)', color: 'var(--neutral-700)' }}
          >
            {SORTS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
          {FILTERS.map(f => (
            <button key={f.id} type="button" onClick={() => setFilter(f.id)} style={{
              padding: isMobile ? '0 14px' : '5px 12px', minHeight: isMobile ? 44 : undefined, borderRadius: 'var(--radius-pill)', fontSize: 'var(--font-size-xs)', fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-body)',
              background: filter === f.id ? 'var(--neutral-800)' : 'var(--brand-white)', color: filter === f.id ? 'var(--brand-white)' : 'var(--neutral-600)', border: '1px solid var(--brand-sand)',
            }}>{f.label} ({fmt(chipCount[f.id])})</button>
          ))}
        </div>
        <p style={{ margin: '0 0 14px', fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)' }}>
          The numbers on the chips count all cravings, not only those matching the search.
          {filter === 'needs_look' && ' Needs a look: the words contain what looks like an email address, phone number, link or @handle. Those can be read but can’t go on Overheard.'}
          {sort === 'worst' && ' Worst matched first: lowest best-match score at the top. Cravings with no score yet come last.'}
        </p>

        {loading ? (
          <div style={{ padding: 'var(--space-xxl)', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)' }}>Loading…</div>
        ) : listError ? (
          <div role="alert" style={{ padding: '12px 14px', borderRadius: 'var(--radius-md)', background: TONE.bad.bg, color: TONE.bad.color, border: `1px solid ${TONE.bad.border}`, fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>
            Couldn&apos;t load the cravings: {listError}
          </div>
        ) : rows.length === 0 ? (
          <div style={{ padding: '36px 0', textAlign: 'center', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)', fontStyle: 'italic' }}>
            {searching || filter !== 'all' ? 'No cravings match this.' : 'No cravings have been recorded yet.'}
          </div>
        ) : (
          <>
            {rows.map(r => (
              <CravingRow
                key={r.id}
                row={r}
                isMobile={isMobile}
                busy={busyIds.has(r.id)}
                result={results[r.id]}
                onAdd={addToOverheard}
                onRemove={removeFromOverheard}
                onSeeOverheard={onSeeOverheard}
              />
            ))}
            {more && (
              <div style={{ textAlign: 'center', marginTop: 8 }}>
                <Btn isMobile={isMobile} onClick={() => loadRows(rows.length)}>Show more</Btn>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
